import React, { useState, useEffect, useCallback } from 'react';
import simplifiedApiService from '../../services/simplifiedApiService';
import CSVPreviewComponent from './CSVPreviewComponent';
import ColumnMappingInterface from './ColumnMappingInterface';
import SavedMappingSelector from './SavedMappingSelector';
import ValidationFeedback from './ValidationFeedback';
import realTimeValidationService from '../../services/realTimeValidationService';
import { API_VERSION_V2 } from '../../config/api.config';

// Import types from the backend
interface ColumnMappingConfig {
  field: string;
  possibleNames: string[];
  required: boolean;
  defaultValue?: any;
  type: 'string' | 'number' | 'percentage' | 'currency' | 'date';
  description?: string;
}

// Mapping configurations for different import types
const salesDataMappingConfig: Record<string, ColumnMappingConfig> = {
  lead_source: {
    field: 'lead_source',
    possibleNames: ['lead source', 'lead source', 'source', 'channel', 'marketing channel'],
    required: true,
    type: 'string',
    description: 'The source or channel that generated the lead',
  },
  contract_total: {
    field: 'contract_total',
    possibleNames: [
      'contract total',
      'contract_total', 
      'total',
      'revenue',
      'sale amount',
      'contract amount',
    ],
    required: true,
    defaultValue: 0,
    type: 'currency',
    description: 'The total contract amount',
  },
  gross_margin_pct: {
    field: 'gross_margin_pct',
    possibleNames: ['gross margin', 'gross margin %', 'margin', 'margin %', 'profit margin'],
    required: false,
    defaultValue: 0,
    type: 'percentage',
    description: 'The gross margin percentage',
  },
  gross_profit: {
    field: 'gross_profit',
    possibleNames: ['gross profit', 'profit', 'net', 'net profit'],
    required: false,
    defaultValue: 0,
    type: 'currency',
    description: 'The gross profit amount',
  },
  trade: {
    field: 'trade',
    possibleNames: ['trade', 'trade type', 'business', 'business type'],
    required: false,
    type: 'string',
    description: 'The type of trade or business',
  },
  job_info: {
    field: 'job_info',
    possibleNames: ['job info', 'job information', 'job', 'project', 'description'],
    required: false,
    type: 'string',
    description: 'Information about the job or project',
  },
  paid_status: {
    field: 'paid_status',
    possibleNames: ['paid', 'status', 'payment status', 'payment_status'],
    required: false,
    type: 'string',
    description: 'Payment or approval status',
  },
};

const adSpendMappingConfig: Record<string, ColumnMappingConfig> = {
  lead_source: {
    field: 'lead_source',
    possibleNames: ['lead source', 'lead source', 'source', 'channel', 'marketing channel'],
    required: true,
    type: 'string',
    description: 'The source or channel that generated the lead',
  },
  spend: {
    field: 'spend',
    possibleNames: ['spend', 'cost', 'amount', 'advertising spend', 'marketing spend'],
    required: true,
    defaultValue: 0,
    type: 'currency',
    description: 'The amount spent on this marketing channel',
  },
  campaign_name: {
    field: 'campaign_name',
    possibleNames: ['campaign', 'campaign name', 'campaign_name', 'name'],
    required: false,
    type: 'string',
    description: 'The name of the marketing campaign',
  },
};

// Define types for API responses
interface ImportSession {
  id: string;
  import_type: 'sales_data' | 'ad_spend_data';
  user_id: string;
  original_filename: string;
  csv_headers: string[];
  status: string;
  validation_results?: any;
  created_at: string;
  updated_at: string;
  metadata?: string;
  file_id?: string;
  file_size?: number;
  mime_type?: string;
  storage_path?: string;
  storage_provider?: string;
  error_message?: string;
  processed_rows?: number;
  total_rows?: number;
  progress_percentage?: number;
  _import_method?: 'standard' | 'streaming';
}

interface ValidationSummary {
  valid_rows: number;
  invalid_rows: number;
  total_rows: number;
  success_rate: number;
  error_count?: number;
  warning_count?: number;
  categories?: Record<string, number>;
  issues?: Array<{
    row: number;
    errors: string[];
  }>;
}

const EnhancedCSVUploadPage: React.FC = () => {
  // File upload state
  const [salesFile, setSalesFile] = useState<File | null>(null);
  const [adspendFile, setAdspendFile] = useState<File | null>(null);

  // CSV data state
  const [currentImportId, setCurrentImportId] = useState('');
  const [importType, setImportType] = useState<'sales_data' | 'ad_spend_data' | ''>('');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<Record<string, any>[]>([]);

  // UI state
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [importResults, setImportResults] = useState<ImportSession | null>(null);
  const [showAllValidation, setShowAllValidation] = useState(false);
  const [processingStep, setProcessingStep] = useState<'upload' | 'preview' | 'mapping' | 'processing' | 'complete'>('upload');

  // Status state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationResults, setValidationResults] = useState<any[]>([]);

  // New state for enhanced features
  const [fileMetadata, setFileMetadata] = useState<any>(null);
  const [validationSummary, setValidationSummary] = useState<ValidationSummary | undefined>(undefined);
  const [isStreamingImport, setIsStreamingImport] = useState(false);
  const [serverSuggestedMappings, setServerSuggestedMappings] = useState<Record<string, string>>({});
  const [serverValidation, setServerValidation] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const [mappingErrors, setMappingErrors] = useState<Record<string, string[]>>({});

  // Get the appropriate field configs based on import type
  const fieldConfigs = importType === 'sales_data' ? salesDataMappingConfig : 
                       importType === 'ad_spend_data' ? adSpendMappingConfig : {};
                       
  const fieldsToMap = importType === 'sales_data' ? Object.keys(salesDataMappingConfig) : 
                      importType === 'ad_spend_data' ? Object.keys(adSpendMappingConfig) : [];

  // Effect to extract CSV data from metadata when importResults changes
  useEffect(() => {
    if (importResults?.metadata) {
      try {
        const metadata = JSON.parse(importResults.metadata);
        if (metadata.parsed_data) {
          setCsvData(metadata.parsed_data);
        }
      } catch (error) {
        console.error('Error parsing metadata:', error);
      }
    }
  }, [importResults]);

  // Effect to validate data when column mappings change
  useEffect(() => {
    if (csvData.length > 0 && Object.keys(fieldConfigs).length > 0) {
      // First check if all required mappings are present
      const completenessValidation = realTimeValidationService.validateMappingCompleteness(
        columnMappings,
        fieldConfigs
      );

      // If we're missing required mappings, just show those errors
      if (completenessValidation) {
        setValidationResults([completenessValidation]);
        return;
      }

      // Only proceed with data validation if we have at least one mapping
      if (Object.keys(columnMappings).length > 0) {
        // Validate a sample of rows (first 10 rows)
        const results = realTimeValidationService.validateCSVData(
          csvData,
          columnMappings,
          fieldConfigs,
          10
        );

        // Generate a validation summary
        const summary = realTimeValidationService.summarizeValidationResults(results);
        console.log('Validation summary:', summary);

        setValidationResults(results);
      } else {
        // Clear validation results if no mappings exist
        setValidationResults([]);
      }
    }
  }, [columnMappings, csvData, fieldConfigs]);

  // Validate data using the backend API
  const validateWithBackend = async () => {
    if (!currentImportId || Object.keys(columnMappings).length === 0) {
      setError('Please upload a file and define column mappings first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call the backend validation API
      const validationResponse = await simplifiedApiService.validateSampleData(
        currentImportId,
        columnMappings,
        undefined,  // rowIndex
        10,  // sampleSize
        isStreamingImport ? 'streaming' : 'standard'
      );

      // Set server validation flag
      setServerValidation(true);

      // Extract validation results
      if (validationResponse.validation_results) {
        try {
          const validationData = typeof validationResponse.validation_results === 'string'
            ? JSON.parse(validationResponse.validation_results)
            : validationResponse.validation_results;

          setValidationSummary(validationData);

          // Convert backend validation format to our format
          const convertedResults = [];

          // Add field-specific validation errors
          if (validationData.field_errors) {
            setMappingErrors(validationData.field_errors);
          }

          // Convert row-specific issues
          if (validationData.issues && Array.isArray(validationData.issues)) {
            for (const issue of validationData.issues) {
              convertedResults.push({
                rowIndex: issue.row,
                issues: issue.errors.map((err: string) => ({
                  field: 'multiple',
                  message: err,
                  severity: 'error',
                  valueType: 'server_validation'
                }))
              });
            }
          }

          setValidationResults(convertedResults);
          setShowAllValidation(true);
        } catch (parseErr) {
          console.error('Error parsing validation results:', parseErr);
          setError('Error parsing validation results from server');
        }
      }

      // Extract suggested mappings from the response if available
      if (validationResponse.suggested_mappings) {
        setServerSuggestedMappings(validationResponse.suggested_mappings);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error validating data');
      } else {
        setError('Unknown error during validation');
      }
    } finally {
      setLoading(false);
    }
  };

  // Run more comprehensive validation on demand when user clicks "Validate All"
  const validateAllData = () => {
    // Try server-side validation first
    if (currentImportId && API_VERSION_V2) {
      validateWithBackend();
      return;
    }

    // Fall back to client-side validation if needed
    if (csvData.length === 0 || Object.keys(columnMappings).length === 0) {
      setError('Please upload a file and define column mappings first');
      return;
    }

    setLoading(true);
    setServerValidation(false);

    // Use a timeout to allow the UI to update
    setTimeout(() => {
      try {
        // Validate all rows
        const results = realTimeValidationService.validateCSVData(
          csvData,
          columnMappings,
          fieldConfigs
        );

        // Also run data quality checks across all data
        const dataQualityResults = realTimeValidationService.checkDataQuality(
          csvData,
          columnMappings,
          fieldConfigs
        );

        setValidationResults([...results, ...dataQualityResults]);
        setShowAllValidation(true);

        // Generate a validation summary
        const summary = realTimeValidationService.summarizeValidationResults([...results, ...dataQualityResults]);

        // Create a validation summary in the format expected by the UI
        setValidationSummary({
          valid_rows: csvData.length - summary.rowsWithErrors,
          invalid_rows: summary.rowsWithErrors,
          total_rows: csvData.length,
          success_rate: Math.round(((csvData.length - summary.rowsWithErrors) / csvData.length) * 100),
          error_count: summary.errorCount,
          warning_count: summary.warningCount
        });

        // Display a summary of what was found
        if (summary.errorCount === 0 && summary.warningCount === 0) {
          setError('');
          alert(`No issues found in ${csvData.length} rows! Your data looks great.`);
        } else {
          setError(`Found ${summary.errorCount} errors and ${summary.warningCount} warnings across ${csvData.length} rows.`);
        }
      } finally {
        setLoading(false);
      }
    }, 50);
  };

  // Handle file selection
  const handleSalesFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setSalesFile(e.target.files[0]);
      setImportType('sales_data');
      setProcessingStep('upload');
      // Reset other state on new file selection
      setColumnMappings({});
      setCsvHeaders([]);
      setCsvData([]);
      setValidationResults([]);
      setImportResults(null);
    }
  };

  const handleAdspendFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setAdspendFile(e.target.files[0]);
      setImportType('ad_spend_data');
      setProcessingStep('upload');
      // Reset other state on new file selection
      setColumnMappings({});
      setCsvHeaders([]);
      setCsvData([]);
      setValidationResults([]);
      setImportResults(null);
    }
  };

  // Handle column mapping changes
  const handleMappingChange = (field: string, csvHeader: string) => {
    setColumnMappings({
      ...columnMappings,
      [field]: csvHeader,
    });
  };

  // Handle saved mapping selection
  const handleSelectSavedMapping = (mapping: Record<string, string>) => {
    setColumnMappings(mapping);
  };

  // Upload file to server
  const uploadFile = async () => {
    if ((!salesFile && !adspendFile) || !importType) return;

    const file = importType === 'sales_data' ? salesFile : adspendFile;
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      let result;
      if (importType === 'sales_data') {
        result = await simplifiedApiService.uploadSalesFile(file);
      } else {
        result = await simplifiedApiService.uploadAdspendFile(file);
      }

      // Check if streaming import was used
      const isStreaming = result._import_method === 'streaming';
      setIsStreamingImport(isStreaming);

      // Store the import method in localStorage for reference
      if (result.id) {
        localStorage.setItem(`import_${result.id}_method`, isStreaming ? 'streaming' : 'standard');
      }

      setCurrentImportId(result.id);
      setCsvHeaders(result.csv_headers || []);
      setImportResults(result);

      // For enhanced API, get additional file metadata
      if (result.file_id) {
        try {
          const metadata = await simplifiedApiService.getFileMetadata(result.id);
          setFileMetadata({
            size: metadata.size || result.file_size,
            encoding: metadata.encoding || 'UTF-8',
            hasBOM: metadata.has_bom || false,
            detectedDelimiter: metadata.detected_delimiter || ','
          });
        } catch (metadataErr) {
          console.warn('Could not fetch file metadata:', metadataErr);
        }
      }

      setProcessingStep('preview');
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error uploading file');
      } else {
        setError('Unknown error uploading file');
      }
    } finally {
      setLoading(false);
    }
  };

  // Process the import
  const processImport = async () => {
    if (!currentImportId || Object.keys(columnMappings).length === 0) {
      setError('Please upload a file and define column mappings first');
      return;
    }

    // Check for validation errors
    const hasErrors = validationResults.some(result =>
      result.issues.some((issue: any) => issue.severity === 'error')
    );

    if (hasErrors) {
      const confirmed = window.confirm('The current mappings have validation errors. Do you want to proceed anyway?');
      if (!confirmed) {
        return;
      }
    }

    setLoading(true);
    setError('');
    setProcessingStep('processing');

    try {
      const result = await simplifiedApiService.processImport(
        currentImportId,
        columnMappings,
        isStreamingImport ? 'streaming' : 'standard'
      );

      // For streaming import, start polling for progress
      if (isStreamingImport && result.needs_polling) {
        // Start polling for status
        startPollingStatus();
      } else {
        // For standard import, just update and complete
        setImportResults(result);

        // Parse validation results if available
        if (result.validation_results) {
          try {
            const validationData = typeof result.validation_results === 'string'
              ? JSON.parse(result.validation_results)
              : result.validation_results;

            setValidationSummary(validationData);
          } catch (parseErr) {
            console.error('Error parsing validation results:', parseErr);
          }
        }

        setProcessingStep('complete');
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error processing import');
      } else {
        setError('Unknown error processing import');
      }
      setProcessingStep('mapping');
    } finally {
      setLoading(false);
    }
  };

  // Start polling for status updates
  const startPollingStatus = useCallback(() => {
    if (!currentImportId) return;

    // Clear any existing interval
    if (pollingInterval !== null) {
      clearInterval(pollingInterval);
    }

    // Set up polling every 2 seconds
    const intervalId = window.setInterval(async () => {
      try {
        const status = await simplifiedApiService.getImportStatus(currentImportId);

        // Update the import results with the latest status
        setImportResults(status);

        // Update progress UI
        if (status.progress_percentage !== undefined) {
          // For visual feedback
        }

        // Check if processing is complete
        if (['completed', 'error', 'cancelled'].includes(status.status)) {
          // Stop polling
          clearInterval(intervalId);
          setPollingInterval(null);

          // If successful, move to complete step
          if (status.status === 'completed') {
            setProcessingStep('complete');
          } else if (status.status === 'error') {
            setError(status.error_message || 'Error processing import');
            setProcessingStep('mapping');
          }
        }
      } catch (err) {
        console.error('Error polling status:', err);
        // Don't stop polling on error, it might be temporary
      }
    }, 2000);

    setPollingInterval(intervalId);

    // Cleanup function
    return () => {
      clearInterval(intervalId);
      setPollingInterval(null);
    };
  }, [currentImportId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval !== null) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Render processing steps
  const renderUploadStep = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <div className="p-6 border rounded-lg shadow-sm bg-white">
        <h2 className="text-lg font-semibold mb-4">Upload Sales Data</h2>
        <p className="text-gray-600 mb-4">
          Upload a CSV file containing sales data. Required fields include lead source and contract total.
        </p>
        <input
          type="file"
          accept=".csv"
          onChange={handleSalesFileChange}
          className="mb-4 block w-full file:mr-4 file:py-2 file:px-4 file:border-0 file:rounded-md file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        <div className="flex justify-end">
          <button
            onClick={() => {
              uploadFile();
            }}
            disabled={!salesFile || loading || importType !== 'sales_data'}
            className="px-4 py-2 bg-blue-500 text-white rounded-md disabled:bg-gray-300 transition-colors"
          >
            {loading ? 'Uploading...' : 'Upload Sales CSV'}
          </button>
        </div>
      </div>

      <div className="p-6 border rounded-lg shadow-sm bg-white">
        <h2 className="text-lg font-semibold mb-4">Upload Ad Spend Data</h2>
        <p className="text-gray-600 mb-4">
          Upload a CSV file containing ad spend data. Required fields include lead source and spend amount.
        </p>
        <input
          type="file"
          accept=".csv"
          onChange={handleAdspendFileChange}
          className="mb-4 block w-full file:mr-4 file:py-2 file:px-4 file:border-0 file:rounded-md file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        <div className="flex justify-end">
          <button
            onClick={() => {
              uploadFile();
            }}
            disabled={!adspendFile || loading || importType !== 'ad_spend_data'}
            className="px-4 py-2 bg-blue-500 text-white rounded-md disabled:bg-gray-300 transition-colors"
          >
            {loading ? 'Uploading...' : 'Upload Ad Spend CSV'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">CSV Data Preview</h2>
        <button
          onClick={() => setProcessingStep('mapping')}
          className="px-4 py-2 bg-blue-500 text-white rounded-md transition-colors"
        >
          Continue to Column Mapping
        </button>
      </div>
      
      <CSVPreviewComponent
        headers={csvHeaders}
        data={csvData}
        maxRows={5}
        fileMetadata={fileMetadata}
        validationSample={validationSummary ? {
          hasIssues: (validationSummary.error_count || 0) > 0,
          rowsWithIssues: validationResults.map(result => result.rowIndex),
          issueCount: validationSummary.error_count
        } : undefined}
      />
      
      <div className="mt-4 p-4 bg-blue-50 rounded-md text-blue-700 text-sm">
        <p className="font-medium mb-1">File Summary:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>File name: {importResults?.original_filename}</li>
          <li>Detected columns: {csvHeaders.length}</li>
          <li>Total rows: {csvData.length}</li>
          <li>Import type: {importType === 'sales_data' ? 'Sales Data' : 'Ad Spend Data'}</li>
        </ul>
      </div>
    </div>
  );

  const renderMappingStep = () => (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Column Mapping</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setProcessingStep('preview')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md transition-colors"
          >
            Back to Preview
          </button>
          <button
            onClick={processImport}
            disabled={loading || Object.keys(columnMappings).length === 0}
            className="px-4 py-2 bg-green-500 text-white rounded-md disabled:bg-gray-300 transition-colors"
          >
            {loading ? 'Processing...' : 'Process Import'}
          </button>
        </div>
      </div>
      
      <SavedMappingSelector
        importType={importType as 'sales_data' | 'ad_spend_data'}
        currentHeaders={csvHeaders}
        onSelectMapping={handleSelectSavedMapping}
        currentMapping={columnMappings}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <ColumnMappingInterface
            csvHeaders={csvHeaders}
            fieldsToMap={fieldsToMap}
            columnMappings={columnMappings}
            fieldConfigs={fieldConfigs}
            onChange={handleMappingChange}
            previewData={csvData}
            validationErrors={mappingErrors}
            isLoading={loading}
            onValidateMapping={validateWithBackend}
            serverSuggestedMappings={serverSuggestedMappings}
          />
        </div>
        
        <div>
          <CSVPreviewComponent 
            headers={csvHeaders}
            data={csvData}
            maxRows={3}
          />
          
          {/* Validation feedback section */}
          <div>
            {validationResults.length > 0 ? (
              <ValidationFeedback
                validationResults={showAllValidation ? validationResults : validationResults.slice(0, 5)}
                maxIssues={5}
                showAllHandler={() => setShowAllValidation(true)}
                validationSummary={validationSummary}
                isProcessing={loading}
                serverValidation={serverValidation}
                onFixClick={(fix) => {
                  // When a suggestion is clicked, apply it to the column mapping
                  const { field, suggestion } = fix;
                  if (!suggestion) return;

                  const suggestedHeader = csvHeaders.find(header =>
                    header.toLowerCase() === suggestion.toLowerCase()
                  );

                  if (suggestedHeader) {
                    // Apply the suggested mapping
                    handleMappingChange(field, suggestedHeader);
                  }
                }}
              />
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">All Good!</h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>No validation issues detected so far. Click &quot;Validate All Data&quot; below for a comprehensive check of your data.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={validateAllData}
                    disabled={loading}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  >
                    {loading ? 'Validating...' : 'Validate All Data'}
                  </button>
                </div>
              </div>
            )}

            {/* Validate all button for when validation results are shown */}
            {validationResults.length > 0 && (
              <div className="flex justify-end mb-4">
                <button
                  onClick={validateAllData}
                  disabled={loading}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                >
                  {loading ? 'Validating...' : 'Validate All Data'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderResults = () => {
    if (!importResults || !importResults.validation_results) return null;
    
    // Parse validation results for better display
    let validationData: {
      summary?: {
        valid_rows: number;
        invalid_rows: number;
        total_rows: number;
        success_rate: number;
      };
      issues?: Array<{
        row: number;
        errors: string[];
      }>;
    } = { summary: { valid_rows: 0, invalid_rows: 0, total_rows: 0, success_rate: 0 }, issues: [] };
    
    try {
      const parsedData = typeof importResults.validation_results === 'string' 
        ? JSON.parse(importResults.validation_results)
        : importResults.validation_results;
      
      validationData = parsedData || validationData;
    } catch (error) {
      console.error('Error parsing validation results:', error);
    }
    
    return (
      <div className="mt-6 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Import Results</h2>
          <button
            onClick={() => {
              // Reset form for new upload
              setProcessingStep('upload');
              setSalesFile(null);
              setAdspendFile(null);
              setColumnMappings({});
              setCsvHeaders([]);
              setCsvData([]);
              setValidationResults([]);
              setImportResults(null);
              setCurrentImportId('');
              setImportType('');
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-md transition-colors"
          >
            Start New Import
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 border rounded-lg shadow-sm bg-white">
            <h3 className="text-lg font-semibold mb-4">Summary</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-md">
                <div className="text-3xl font-bold text-green-700 mb-1">
                  {validationData?.summary?.valid_rows || 0}
                </div>
                <div className="text-sm text-green-600">Rows Imported</div>
              </div>
              
              <div className="p-4 bg-red-50 rounded-md">
                <div className="text-3xl font-bold text-red-700 mb-1">
                  {validationData?.summary?.invalid_rows || 0}
                </div>
                <div className="text-sm text-red-600">Rows Failed</div>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-md">
                <div className="text-3xl font-bold text-blue-700 mb-1">
                  {validationData?.summary?.total_rows || 0}
                </div>
                <div className="text-sm text-blue-600">Total Rows</div>
              </div>
              
              <div className="p-4 bg-yellow-50 rounded-md">
                <div className="text-3xl font-bold text-yellow-700 mb-1">
                  {validationData?.summary?.success_rate || 0}%
                </div>
                <div className="text-sm text-yellow-600">Success Rate</div>
              </div>
            </div>
            
            <div className="mt-4">
              <h4 className="font-medium mb-2">File Details</h4>
              <ul className="text-sm space-y-1">
                <li><span className="text-gray-500">File name:</span> {importResults.original_filename}</li>
                <li><span className="text-gray-500">Import ID:</span> {importResults.id}</li>
                <li><span className="text-gray-500">Import Type:</span> {importResults.import_type === 'sales_data' ? 'Sales Data' : 'Ad Spend Data'}</li>
                <li><span className="text-gray-500">Imported on:</span> {new Date(importResults.updated_at).toLocaleString()}</li>
              </ul>
            </div>
          </div>
          
          <div className="p-6 border rounded-lg shadow-sm bg-white">
            <h3 className="text-lg font-semibold mb-4">Issues</h3>
            
            {validationData.issues && validationData.issues.length > 0 ? (
              <div className="max-h-72 overflow-auto">
                {validationData.issues.map((issue: any, index: number) => (
                  <div key={index} className="p-3 border-b last:border-b-0">
                    <div className="font-medium">Row {issue.row}</div>
                    <ul className="mt-1 text-sm space-y-1">
                      {issue.errors.map((error: string, errorIndex: number) => (
                        <li key={errorIndex} className="text-red-600">
                          • {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-green-600">
                No issues found! All data was imported successfully.
              </div>
            )}
          </div>
        </div>
        
        <div className="p-6 border rounded-lg shadow-sm bg-white">
          <h3 className="text-lg font-semibold mb-4">Next Steps</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a 
              href={`/reports?import_id=${importResults.id}`}
              className="p-4 bg-indigo-50 border border-indigo-100 rounded-md hover:bg-indigo-100 transition-colors"
            >
              <h4 className="font-medium text-indigo-700 mb-1">View Reports</h4>
              <p className="text-sm text-indigo-600">Generate marketing performance reports using this data</p>
            </a>
            
            <a
              href={importType === 'sales_data' ? `/view/sales/${importResults.id}` : `/view/adspend/${importResults.id}`}
              className="p-4 bg-purple-50 border border-purple-100 rounded-md hover:bg-purple-100 transition-colors"
            >
              <h4 className="font-medium text-purple-700 mb-1">View Raw Data</h4>
              <p className="text-sm text-purple-600">Examine the imported data directly</p>
            </a>
            
            <a
              href="/upload-simplified"
              className="p-4 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors"
            >
              <h4 className="font-medium text-blue-700 mb-1">Upload Another File</h4>
              <p className="text-sm text-blue-600">Import additional data to enhance your reports</p>
            </a>
          </div>
        </div>
      </div>
    );
  };

  // Define step progress
  const steps = [
    { id: 'upload', label: 'Upload File' },
    { id: 'preview', label: 'Preview Data' },
    { id: 'mapping', label: 'Map Columns' },
    { id: 'processing', label: 'Processing' },
    { id: 'complete', label: 'Results' },
  ];

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Enhanced CSV Upload (Phase 2)</h1>
      
      {/* Progress indicator */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          {steps.map((step, index) => (
            <div 
              key={step.id} 
              className={`flex flex-col items-center ${
                steps.findIndex(s => s.id === processingStep) >= index
                  ? 'text-blue-600'
                  : 'text-gray-400'
              }`}
            >
              <div 
                className={`w-8 h-8 flex items-center justify-center rounded-full mb-1 ${
                  processingStep === step.id
                    ? 'bg-blue-100 border-2 border-blue-500'
                    : steps.findIndex(s => s.id === processingStep) > index
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100'
                }`}
              >
                {steps.findIndex(s => s.id === processingStep) > index ? (
                  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              <div className="text-xs text-center">{step.label}</div>
            </div>
          ))}
        </div>
        <div className="relative flex h-1 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="absolute top-0 bottom-0 left-0 bg-blue-500 transition-all duration-500"
            style={{ 
              width: `${(steps.findIndex(s => s.id === processingStep) / (steps.length - 1)) * 100}%` 
            }}
          ></div>
        </div>
      </div>

      {error && <div className="p-4 mb-6 bg-red-100 border border-red-400 text-red-700 rounded-md">{error}</div>}

      {/* Render different UI based on the current step */}
      {processingStep === 'upload' && renderUploadStep()}
      {processingStep === 'preview' && renderPreviewStep()}
      {processingStep === 'mapping' && renderMappingStep()}
      {processingStep === 'processing' && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">Processing Your Data</h2>

          {/* Streaming import progress indicator */}
          {isStreamingImport && importResults?.progress_percentage !== undefined && (
            <div className="mb-6 max-w-md mx-auto">
              <div className="flex justify-between text-sm mb-1">
                <span>{importResults.processed_rows || 0} of {importResults.total_rows || 0} rows</span>
                <span>{importResults.progress_percentage}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${importResults.progress_percentage || 0}%` }}
                ></div>
              </div>
              {importResults.status === 'processing' && (
                <p className="text-sm text-gray-500 mt-2">
                  Processing {importResults.import_type === 'sales_data' ? 'sales' : 'ad spend'} data...
                </p>
              )}
            </div>
          )}

          <p className="text-gray-600">
            Please wait while we process your file. This may take a few moments...
          </p>

          {importResults?.error_message && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded max-w-md mx-auto">
              <p className="text-red-700">{importResults.error_message}</p>
            </div>
          )}
        </div>
      )}
      {processingStep === 'complete' && renderResults()}
    </div>
  );
};

export default EnhancedCSVUploadPage;